export type DocumentType = "purchase_order" | "sale_order" | "expense_order";

export type DocumentApproverScope = "all_permitted" | "selected";

export interface DocumentApprovalRuleValues {
  approverId: string;
  minimum?: number | null;
  maximum?: number | null;
}

export interface AmountRange {
  minimum?: number | null;
  maximum?: number | null;
}

const AMOUNT_PRECISION = 2;
const CONTIGUITY_TOLERANCE = 0.01;

export function parseDocumentApproverScope(
  scope?: string | null
): DocumentApproverScope {
  return scope === "selected" ? "selected" : "all_permitted";
}

export function parseCriteria(criteria: unknown): AmountRange {
  const range =
    criteria && typeof criteria === "object"
      ? (criteria as Record<string, any>).amount_range
      : null;

  if (!range || typeof range !== "object") return {};

  return {
    minimum: parseAmount(range.minimum),
    maximum: parseAmount(range.maximum),
  };
}

export function buildCriteria({
  minimum,
  maximum,
}: AmountRange): Record<string, unknown> {
  const range: Record<string, string> = {};

  if (minimum != null) range.minimum = formatAmount(minimum);
  if (maximum != null) range.maximum = formatAmount(maximum);

  return Object.keys(range).length ? { amount_range: range } : {};
}

function formatAmount(value: number): string {
  return Math.max(0, value).toFixed(AMOUNT_PRECISION);
}

export function findRangeGaps(
  rules: DocumentApprovalRuleValues[]
): AmountRange[] {
  if (!rules.length) return [];

  const ranges = rules
    .map(({ minimum, maximum }) => ({
      minimum: minimum ?? 0,
      maximum: maximum ?? null,
    }))
    .toSorted((a, b) => a.minimum - b.minimum);

  const gaps: AmountRange[] = [];
  let covered: number | null = 0;

  ranges.forEach(({ minimum, maximum }) => {
    if (covered == null) return;

    if (minimum - covered > CONTIGUITY_TOLERANCE) {
      gaps.push({ minimum: covered === 0 ? null : covered, maximum: minimum });
    }

    covered = maximum == null ? null : Math.max(covered, maximum);
  });

  if (covered != null) gaps.push({ minimum: covered, maximum: null });

  return gaps;
}

function parseAmount(value: unknown): number | null {
  if (value == null || value === "") return null;

  const amount = Number(value);

  return isNaN(amount) ? null : amount;
}

export function availableDocumentApprovalTypes(features: {
  purchaseOrders?: boolean | null;
  saleOrders?: boolean | null;
  expenses?: boolean | null;
}): DocumentType[] {
  return [
    features.purchaseOrders && "purchase_order",
    features.saleOrders && "sale_order",
    features.expenses && "expense_order",
  ].filter(Boolean) as DocumentType[];
}

export interface DocumentApprovalError {
  path: string;
  message: string;
}

export function mapDocumentApprovalErrorPaths(
  errors: DocumentApprovalError[],
  documentTypes: DocumentType[]
): DocumentApprovalError[] {
  return errors.map((error) => ({
    ...error,
    path: mapDocumentApprovalErrorPath(error.path, documentTypes),
  }));
}

const RULE_PATH =
  /^documentApprovalRules\[(\d+)\]\.rules\[(\d+)\](?:\.(\w+))?$/;

function mapDocumentApprovalErrorPath(
  path: string,
  documentTypes: DocumentType[]
) {
  const rule = RULE_PATH.exec(path);
  if (rule) {
    const documentType = documentTypes[Number(rule[1])];
    if (!documentType) return path;

    const field = rule[3] === "criteria" ? "maximum" : "approverId";

    return `${documentType}.rules[${rule[2]}].${field}`;
  }

  return path;
}
